import { useCallback, useEffect, useState } from "react";
import { api, apiError, imgUrl } from "@/lib/api";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { openUpgradeSheet } from "@/components/UpgradeSheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Contact, Copy, Instagram, Ghost, Phone, Mail, Facebook, Coins, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const CONTACT_OPTIONS = [
  ["phone", "Phone / WhatsApp", Phone], ["instagram", "Instagram", Instagram], ["snapchat", "Snapchat", Ghost],
  ["email", "Email", Mail], ["facebook", "Facebook", Facebook],
];

export function useContactExchange(matchId, enabled) {
  const [state, setState] = useState(null);
  const load = useCallback(() => api.get(`/matches/${matchId}/contacts`).then(({ data }) => setState(data)).catch(() => {}), [matchId]);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);
  return [state, setState, load];
}

function CostLine({ state }) {
  if (state.theirs) return <p data-testid="contact-cost" className="text-xs text-emerald-300">Replying is free.</p>;
  if (state.premium) return <p data-testid="contact-cost" className="text-xs text-amber-200 flex items-center gap-1.5"><Crown className="h-3.5 w-3.5" /> Included with your Speed Pass.</p>;
  if (state.cost === 0) return <p data-testid="contact-cost" className="text-xs text-emerald-300">Already unlocked for this match.</p>;
  return (
    <p data-testid="contact-cost" className="text-xs text-amber-200 flex items-center gap-1.5">
      <Coins className="h-3.5 w-3.5" /> Uses 1 Speed Token to send yours and see theirs · you have {state.tokens}
    </p>
  );
}

function RequestDialog({ open, setOpen, state, matchId, setState, partnerName }) {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPicked(state.available); }, [open, state.available]);
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const send = async () => {
    setBusy(true);
    try { setState((await api.post(`/matches/${matchId}/contacts/exchange`, { fields: picked })).data); setOpen(false); toast.success(state.theirs ? "Cards swapped!" : "Contact card sent"); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 402) { setOpen(false); openUpgradeSheet({ title: "Unlock contact cards", message: d?.message, reason: "contacts" }); }
      else toast.error(apiError(d));
    } finally { setBusy(false); }
  };
  const none = state.available.length === 0;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="contact-request-dialog" className="glass border-amber-500/20 rounded-3xl max-w-sm">
        <DialogHeader className="text-left">
          <DialogTitle className="font-display">{state.theirs ? `Send your card to ${partnerName}` : "Request contact card"}</DialogTitle>
          <DialogDescription className="text-emerald-100/60">
            {state.theirs ? `${partnerName} already sent theirs — pick what to share back.` : `Pick what to share. ${partnerName} sees it only after sending theirs back.`}
          </DialogDescription>
        </DialogHeader>
        {none ? (
          <p data-testid="contact-none" className="text-sm text-slate-300">Add a phone, Instagram, Snapchat, email or Facebook in your <a href="/profile" className="text-amber-300 underline">Profile</a> first — it's free.</p>
        ) : (
          <div className="space-y-2">
            {CONTACT_OPTIONS.filter(([k]) => state.available.includes(k)).map(([k, label, Icon]) => (
              <label key={k} data-testid={`contact-pick-${k}`} className="flex items-center gap-3 rounded-xl border border-white/10 px-3 h-11 cursor-pointer hover:bg-white/5">
                <Checkbox checked={picked.includes(k)} onCheckedChange={() => toggle(k)} />
                <Icon className="h-4 w-4 text-amber-300" /> <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        )}
        <CostLine state={state} />
        <button data-testid="contact-send-btn" disabled={busy || none || picked.length === 0} onClick={send}
          className="btn-gold rounded-full h-11 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {state.theirs ? "Send my card back" : "Send request"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

export function ExchangeContactsButton({ matchId, state, setState, partnerName }) {
  const [open, setOpen] = useState(false);
  if (!state || state.revealed) return null;
  return (
    <>
      <button data-testid="exchange-contacts-btn" onClick={() => setOpen(true)} disabled={state.mine}
        className={`w-full mb-2 h-10 rounded-full text-sm font-semibold flex items-center justify-center gap-2 border transition-colors ${state.mine ? "border-amber-400/30 text-amber-200/80 bg-amber-400/5" : "border-sky-400/40 text-sky-100 hover:bg-sky-400/10"}`}>
        <Contact className="h-4 w-4" />
        {state.mine ? `Contact card sent — waiting for ${partnerName}` : state.theirs ? `${partnerName} sent a contact card — reply free` : "Request Contact Card"}
        {!state.mine && !state.theirs && !state.premium && state.cost > 0 && <span className="text-[10px] rounded-full bg-amber-400/15 text-amber-200 px-2 py-0.5">1 token</span>}
      </button>
      <RequestDialog open={open} setOpen={setOpen} state={state} matchId={matchId} setState={setState} partnerName={partnerName} />
    </>
  );
}

const digits = (p) => (p || "").replace(/[^\d]/g, "");

function CardLinks({ card }) {
  const row = "flex items-center gap-2 truncate";
  return (
    <div className="mt-3 space-y-1.5 text-sm flex-1 min-h-0">
      {card.phone && <p data-testid="contact-card-phone" className={row}><Phone className="h-4 w-4 text-emerald-300 shrink-0" /> {card.phone}</p>}
      {card.instagram && <a data-testid="contact-card-instagram" href={`https://instagram.com/${card.instagram}`} target="_blank" rel="noreferrer" className={`${row} hover:text-pink-300`}><Instagram className="h-4 w-4 text-pink-300 shrink-0" /> @{card.instagram}</a>}
      {card.snapchat && <a data-testid="contact-card-snapchat" href={`https://www.snapchat.com/add/${card.snapchat}`} target="_blank" rel="noreferrer" className={`${row} hover:text-yellow-200`}><Ghost className="h-4 w-4 text-yellow-300 shrink-0" /> {card.snapchat}</a>}
      {card.email && <a data-testid="contact-card-email" href={`mailto:${card.email}`} className={`${row} hover:text-sky-200`}><Mail className="h-4 w-4 text-sky-300 shrink-0" /> {card.email}</a>}
      {card.facebook && <a data-testid="contact-card-facebook" href={`https://www.facebook.com/${encodeURIComponent(card.facebook)}`} target="_blank" rel="noreferrer" className={`${row} hover:text-blue-300`}><Facebook className="h-4 w-4 text-blue-400 shrink-0" /> {card.facebook}</a>}
    </div>
  );
}

export function ContactCardReveal({ card }) {
  const [flipped, setFlipped] = useState(false);
  useEffect(() => { const t = setTimeout(() => setFlipped(true), 150); return () => clearTimeout(t); }, []);
  const copy = async () => {
    try { await navigator.clipboard.writeText(card.phone); toast.success("Number copied"); }
    catch { toast.error("Couldn't copy — long-press the number instead"); }
  };
  return (
    <div data-testid="contact-card" className="flex justify-center my-4 [perspective:1200px]">
      <div className={`relative w-full max-w-xs h-72 transition-transform duration-700 [transform-style:preserve-3d] ${flipped ? "[transform:rotateY(180deg)]" : ""}`}>
        <div className="absolute inset-0 rounded-3xl btn-gold flex flex-col items-center justify-center [backface-visibility:hidden]">
          <Contact className="h-10 w-10" /><p className="font-display font-black mt-2">Contact cards swapped</p>
        </div>
        <div className="absolute inset-0 rounded-3xl glass border-amber-400/40 p-5 flex flex-col [backface-visibility:hidden] [transform:rotateY(180deg)]">
          <div className="flex items-center gap-3">
            <img src={imgUrl(card.photo)} alt="" className="h-12 w-12 rounded-full object-cover" />
            <p className="font-display font-bold text-lg flex items-center gap-1.5">{card.name} {card.verified && <VerifiedBadge size="xs" />}</p>
          </div>
          <CardLinks card={card} />
          {card.phone && (
            <div className="flex gap-2">
              <button data-testid="contact-card-copy" onClick={copy} className="flex-1 h-10 rounded-full border border-white/15 text-sm flex items-center justify-center gap-1.5 hover:bg-white/5"><Copy className="h-4 w-4" /> Copy Number</button>
              <a data-testid="contact-card-whatsapp" href={`https://wa.me/${digits(card.phone)}`} target="_blank" rel="noreferrer" className="flex-1 h-10 rounded-full bg-[#25D366] text-black text-sm font-semibold flex items-center justify-center gap-1.5">WhatsApp</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
